import {
  matchPublicPath,
  publicPath,
  type PublicPageKey,
  type SiteLanguage,
  type SiteLayout,
} from "./publicPages";

/**
 * Which host serves what, and how a link is written on each of them.
 *
 * - zettl.cz (and www): the public site. Sign-in lives on app.zettl.cz, so app
 *   links are absolute there.
 * - app.zettl.cz: the app. Public pages are absolute links to zettl.cz.
 * - zettlapp.com: only redirects (vercel.json), except the MCP connector and
 *   its OAuth sign-in, which stay there for good.
 * - Previews (pr-<N>.zettlapp.com, pr-<N>.zettl.cz), localhost: everything on
 *   one host, root layout, all links relative, so a preview or the UI harness
 *   never leaves its host.
 */

export const PUBLIC_SITE_HOST = "zettl.cz";
export const APP_HOST = "app.zettl.cz";
export const LEGACY_HOST = "zettlapp.com";

/**
 * The layout the SEO files, previews and shared (English) pages use. "root"
 * since phase B; "legacy" (with the old vercel.json) rolls the move back.
 */
export const SITE_LAYOUT = "root" as SiteLayout;
/** The host a prerendered page is written for when its URL does not decide it. */
export const SITE_HOST = SITE_LAYOUT === "root" ? PUBLIC_SITE_HOST : LEGACY_HOST;

export function isPublicSiteHost(host: string): boolean {
  return host === PUBLIC_SITE_HOST || host === `www.${PUBLIC_SITE_HOST}`;
}

export function isAppHost(host: string): boolean {
  return host === APP_HOST;
}

/** Hosts that serve the root layout on their own (no app split): zettl.cz previews, dev. */
function servesRootLayout(host: string): boolean {
  return (
    isPublicSiteHost(host) ||
    /^pr-\d+\.zettl\.cz$/.test(host) ||
    host === "localhost" ||
    host === "127.0.0.1"
  );
}

/** The layout public links use on this host. */
export function linkLayout(host: string): SiteLayout {
  return servesRootLayout(host) || isAppHost(host) ? "root" : SITE_LAYOUT;
}

/** A path inside the app (`/cs/auth?tab=register`), written for this host. */
export function appHref(path: string, host: string): string {
  return isPublicSiteHost(host) ? `https://${APP_HOST}${path}` : path;
}

/** A public page, written for this host. */
export function publicHref(key: PublicPageKey, lang: SiteLanguage, host: string): string {
  const path = publicPath(key, lang, linkLayout(host));
  return isAppHost(host) ? `https://${PUBLIC_SITE_HOST}${path}` : path;
}

/**
 * Any link the public pages write in the old style, `localePath(locale, "/privacy")`
 * or `"/auth?tab=register"` or `"/#cenik"`: public pages go to their slug for
 * this host, everything else is an app path.
 */
export function resolveSiteLink(to: string, lang: SiteLanguage, host: string): string {
  const [, path = "", rest = ""] = to.match(/^([^?#]*)(.*)$/) ?? [];
  const bare = path.replace(/^\/(cs|en)(?=\/|$)/, "") || "/";
  const normalized = bare.startsWith("/") ? bare : `/${bare}`;
  const page = matchPublicPath(`/${lang}${normalized === "/" ? "" : normalized}`);
  if (page) return `${publicHref(page.key, lang, host)}${rest}`;
  return appHref(`/${lang}${normalized}${rest}`, host);
}

/**
 * The MCP connector's address. It stays on zettlapp.com for good: its OAuth
 * issuer and metadata name that origin, and existing Claude connectors use it.
 * Previews and dev keep their own origin, as before.
 */
export function mcpConnectorUrl(host: string, origin: string): string {
  const production = host === LEGACY_HOST || isPublicSiteHost(host) || isAppHost(host);
  return `${production ? `https://${LEGACY_HOST}` : origin}/mcp`;
}

let renderHost: string | null = null;

/** The prerender sets the host a page is written for; the browser uses its own. */
export function setRenderHost(host: string | null): void {
  renderHost = host;
}

export function currentHost(): string {
  if (renderHost) return renderHost;
  if (typeof window !== "undefined") return window.location.hostname;
  return SITE_HOST;
}
